import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

import { ReactComponent as AlertIcon } from '../../assets/icons/alert-icon.svg';
import { ReactComponent as SuccessIcon } from '../../assets/icons/success-tick.svg';
import { ReactComponent as CloseIcon } from '../../assets/icons/toaster-close-icon.svg';

interface ToastMessageProperties {
  open: boolean;
  type: 'error' | 'success'; // Define more types if required
  message: string;
  onClose: () => void;
  onCloseTimer?: number;
}

/**
 * Customized toast message component.
 * @param {ToastMessageProperties} properties - The properties.
 * @returns {React.ReactPortal | null} The rendered toast portal or null.
 */
function ToastMessage(properties: ToastMessageProperties) {
  const { open, type, message, onClose, onCloseTimer } = properties;
  const containerReference = useRef<HTMLDivElement>(null);

  /**
   * Handle click outside the toast container
   * @param {MouseEvent} event - The mouse event.
   */
  const handleClickOutside = (event: MouseEvent) => {
    if (containerReference.current && !containerReference.current.contains(event.target as Node)) {
      onClose(); // Call the onClose function when clicked outside
    }
  };

  useEffect(() => {
    // Add event listener for clicks
    document.addEventListener('mousedown', handleClickOutside);
    // Cleanup event listener on component unmount
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (onCloseTimer && open && onClose) {
      const timer = setTimeout(() => {
        onClose();
      }, onCloseTimer || 3000);

      return () => clearTimeout(timer); // Cleanup the timer if the component unmounts
    }

    return undefined; // Explicitly return undefined when notificationCloseTimer is false
  }, [open, onCloseTimer]);

  if (!open) return null;

  return ReactDOM.createPortal(
    <div className="toast-message-wrap">
      <div className={`toast-message-container ${type}`} ref={containerReference}>
        <span className="toast-icon">
          {type === 'success' ? <SuccessIcon /> : <AlertIcon />}
        </span>
        <p>{message}</p>
        <span className="toast-close" role="presentation" onClick={onClose}>
          <CloseIcon />
        </span>
      </div>
    </div>,
    document.body,
  );
}

ToastMessage.defaultProps = {
  onCloseTimer: 6000,
};

export default ToastMessage;
